import classNames from 'clsx'
import { bool, func, shape, string } from 'prop-types'

import { PLACEHOLDER_URL } from 'shared/consts/marketplace/orders'
import store from 'shared/store'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import Image from 'views/components/atoms/Image'
import {
  currencyToSymbol,
  formatPriceString,
} from 'views/components/atoms/Price/helpers'
import ItemStars from 'views/components/organisms/PreQuerySuggestions/PreQueryListItem/ItemStars'
import {
  getLegalSupNumberForTenant,
  getSuggestionParameters,
} from 'views/components/organisms/SearchBox/helper'
import { isProductAvailable } from 'views/utils/productUtils'
import { SuggestContentES_v3 as SuggestContentES } from 'views/utils/propTypes'
import { isRequired } from 'views/utils/propTypeUtils'

import SearchSuggestionName from './SearchSuggestionName'
import SearchSuggestionPrice from './SearchSuggestionPrice'

import './SearchBoxListItem.scss'

const SearchBoxListItem = ({
  mobile,
  item: {
    id,
    name,
    prices,
    deeplink,
    thumbnailURL,
    hasMultipleVariants,
    averageRating,
    discountInPercent,
    listPriceFormatted,
    prescriptionMedicine,
    highlightedLabel,
    stockStatus,
  },
  queryStr,
  handleItemClick,
  focused,
  last,
}) => {
  const {
    publicConfig: { tenant, locale, currencySymbolRight, currency },
  } = store.getPublicRuntimeConfig()

  const classList = classNames(
    'a-link a-link--no-underline o-SearchBox__item e-SearchBox__item l-flex ',
    {
      'o-SearchBox__item--focused': focused,
      'u-border--bottom@medium o-SearchBox__item--border-bottom': !last,
      'o-SearchBox__item--highlighted u-border@medium u-border-radius':
        Boolean(highlightedLabel) && !mobile,
    }
  )

  const isDiscountAvaliable = discountInPercent > 0
  const isAvailable = isProductAvailable(stockStatus)

  const { showDiscountBubble, showReducedPrice, showRatings } =
    getSuggestionParameters(tenant)

  const renderDiscountPercentageBubble =
    isDiscountAvaliable && showDiscountBubble

  const productUrl = deeplink.indexOf('/', 0) === 0 ? deeplink : `/${deeplink}`

  const currencySymbol = currencyToSymbol(currency)

  const listPriceToShow = listPriceFormatted
    ? formatPriceString({
        amount: listPriceFormatted,
        locale,
        currencySymbol,
        symbolRight: currencySymbolRight,
      })
    : ''

  const productThumbnailUrl = prescriptionMedicine
    ? PLACEHOLDER_URL
    : thumbnailURL

  return (
    <li
      key={`product-${id}`}
      data-product-id={id}
      data-qa-id="product-codes-list-in-suggest"
    >
      <a
        className={classList}
        href={productUrl}
        onClick={() => handleItemClick?.()}
        data-clientside-hook="SearchBoxListItemLink"
        data-qa-id="suggest-items"
      >
        <Image
          src={productThumbnailUrl}
          srcWidth={128}
          srcHeight={128}
          className="o-SearchBox__image u-border-radius u-background-color--background u-margin--right l-flex__fixed"
          alt={name}
        />
        <span
          className={classNames('l-flex__primary', {
            'l-flex--vertical-stretch l-flex': showRatings,
          })}
        >
          <span className="o-SearchBox__label">
            {highlightedLabel && !mobile && (
              <>
                <span className="u-color--dark u-hidden@small-tiny">
                  <i>{highlightedLabel}</i>
                </span>
                <br />
              </>
            )}
            <SearchSuggestionName
              name={name}
              queryStr={queryStr}
              concat={mobile}
            />
          </span>
          {showRatings ? <ItemStars rating={averageRating} /> : null}
        </span>
        {isAvailable && (
          <span className="u-margin-x-small--left l-flex__fixed">
            <div
              className={classNames(
                'o-SearchBox__price e-SearchBox__price a-h2 u-no-margin u-text-align--right u-font-weight--normal u-no-wrap',
                {
                  'l-flex l-flex--justify-content--end':
                    renderDiscountPercentageBubble,
                }
              )}
            >
              {Boolean(discountInPercent) && renderDiscountPercentageBubble && (
                <span className="l-flex">
                  <DiscountBadge
                    discount={discountInPercent}
                    ellipse
                    rounded
                    smaller
                    className="u-vertical-align--super"
                  >
                    <span>
                      &minus;<strong>{discountInPercent}</strong>%
                    </span>
                  </DiscountBadge>
                </span>
              )}
              <span className="u-margin-x-small--left">
                <SearchSuggestionPrice
                  prices={prices}
                  hasMultipleVariants={hasMultipleVariants}
                />
              </span>
            </div>
            {showReducedPrice && isDiscountAvaliable && listPriceToShow && (
              <div className="e-SearchBox__normal-price u-text-align--right">
                <span className="u-text-decoration--line-through">
                  {listPriceToShow}
                  <sup>{getLegalSupNumberForTenant(tenant)}</sup>
                </span>
              </div>
            )}
          </span>
        )}
      </a>
    </li>
  )
}

SearchBoxListItem.propTypes = {
  item: shape(isRequired(SuggestContentES)).isRequired,
  mobile: bool,
  queryStr: string,
  handleItemClick: func,
  focused: bool,
  last: bool,
}

SearchBoxListItem.defaultProps = {
  mobile: null,
  queryStr: null,
  handleItemClick: null,
  focused: false,
  last: true,
}

export default SearchBoxListItem
